/**
 * Parses a decimal-GEL input string into integer **tetri** (1 GEL = 100 tetri),
 * the reverse of {@link formatGel} and the write-edge companion to it.
 *
 * All money arithmetic happens in integer tetri, so user input has to cross the
 * boundary exactly once, here, with no float drift. Parsing is done on the digit
 * strings (never `parseFloat`) so `12.34` is unambiguously `1234` tetri.
 *
 * Sub-tetri precision is rejected: the ledger's smallest unit is one tetri
 * (0.01 GEL), so `12.345` is not representable and must be refused rather than
 * silently rounded. Trailing zeros beyond two decimals are harmless (`12.340`
 * is `12.34`) and accepted.
 *
 * Amounts are entered unsigned — a line's side (debit/credit) carries the sign —
 * so a real negative is rejected. `-0` normalises to `0`.
 *
 * Returns a discriminated result so callers can tell *empty* (nothing typed yet)
 * apart from *invalid* (bad input) and surface the right message.
 */
export type ParseGelResult =
    | { ok: true; tetri: number }
    | { ok: false; reason: 'empty' | 'invalid' | 'sub-tetri' | 'negative' };

export const parseGelToTetri = (input: string): ParseGelResult => {
    const trimmed = input.trim();
    if (trimmed === '') {
        return { ok: false, reason: 'empty' };
    }

    // Tolerate a leading ₾, grouping commas, and internal spaces so a pasted
    // "₾ 1,234.56" round-trips; everything else must be plain digits + one dot.
    const cleaned = trimmed.replace(/[₾\s,]/g, '');
    if (cleaned === '') {
        return { ok: false, reason: 'empty' };
    }
    if (!/^-?\d*(?:\.\d+)?$/.test(cleaned) || !/\d/.test(cleaned)) {
        return { ok: false, reason: 'invalid' };
    }

    const negative = cleaned.startsWith('-');
    const unsigned = negative ? cleaned.slice(1) : cleaned;
    const [wholeRaw = '', fracRaw = ''] = unsigned.split('.');
    const whole = wholeRaw === '' ? '0' : wholeRaw;

    // Any significant digit past the hundredths place is finer than one tetri.
    if (fracRaw.length > 2 && /[1-9]/.test(fracRaw.slice(2))) {
        return { ok: false, reason: 'sub-tetri' };
    }

    const frac2 = fracRaw.slice(0, 2).padEnd(2, '0');
    const tetri = Number(whole) * 100 + Number(frac2);

    if (negative && tetri !== 0) {
        return { ok: false, reason: 'negative' };
    }

    return { ok: true, tetri };
};
